// Oreia icon set — ported from design_handoff_oreia/reference/icons.jsx.
//
// Stroke icons that inherit currentColor, so a parent's `color` drives them. Kept as inline SVG
// rather than an icon dependency because these are the app's own drawings and the iOS app uses the
// same shapes; a third-party set would drift from it.

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const stroke = (props: IconProps): IconProps => ({
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  ...props,
});

export const IconSpark = (p: IconProps) => (
  <svg {...stroke(p)} fill="currentColor" stroke="none">
    <path d="M12 2c.45 3.6 2.1 5.25 5.7 5.7-3.6.45-5.25 2.1-5.7 5.7-.45-3.6-2.1-5.25-5.7-5.7C9.9 7.25 11.55 5.6 12 2Z" />
    <circle cx="18.5" cy="17" r="1.3" fill="currentColor" />
  </svg>
);

export const IconLock = (p: IconProps) => (
  <svg {...stroke(p)}>
    <rect x="5" y="11" width="14" height="9" rx="2.5" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

export const IconCalendar = (p: IconProps) => (
  <svg {...stroke(p)}>
    <rect x="4" y="5.5" width="16" height="15" rx="2.5" />
    <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
  </svg>
);

export const IconArrow = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const IconArrowUp = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);

export const IconChevronLeft = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

export const IconChevronRight = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M9 5l7 7-7 7" />
  </svg>
);

export const IconArrowUpRight = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M7 17 17 7M9 7h8v8" />
  </svg>
);

export const IconHome = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M3 11l9-7 9 7M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
  </svg>
);

export const IconBook = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H12v17H6.5A2.5 2.5 0 0 0 4 22V5.5ZM20 5.5A2.5 2.5 0 0 0 17.5 3H12v17h5.5a2.5 2.5 0 0 1 2.5 2.5V5.5Z" />
  </svg>
);

export const IconCompass = (p: IconProps) => (
  <svg {...stroke(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5z" />
  </svg>
);

export const IconClock = (p: IconProps) => (
  <svg {...stroke(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);

export const IconUser = (p: IconProps) => (
  <svg {...stroke(p)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20c1.6-3.8 13.4-3.8 15 0" />
  </svg>
);

export const IconOreia = (p: IconProps) => (
  <svg {...stroke(p)} fill="currentColor" stroke="none">
    <path d="M12 1.5c.5 4.2 2.3 6 6.5 6.5-4.2.5-6 2.3-6.5 6.5-.5-4.2-2.3-6-6.5-6.5 4.2-.5 6-2.3 6.5-6.5Z" />
    <circle cx="19" cy="18" r="1.5" />
  </svg>
);

export const IconWave = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M3 12h2M8 7v10M12 4v16M16 8v8M21 11v2" />
  </svg>
);

export const IconImage = (p: IconProps) => (
  <svg {...stroke(p)}>
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="M21 16l-5-5L5 20" />
  </svg>
);

export const IconMic = (p: IconProps) => (
  <svg {...stroke(p)}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </svg>
);

export const IconClose = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

/**
 * Three bars, filled — no stroke, no circular chrome. Used for "there is a panel behind this"
 * (conversations, the collapsed rail) so the mark itself is the button.
 */
export const IconMenu = (p: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden {...p}>
    <rect x="4" y="6.25" width="16" height="2.1" rx="1.05" />
    <rect x="4" y="10.95" width="16" height="2.1" rx="1.05" />
    <rect x="4" y="15.65" width="16" height="2.1" rx="1.05" />
  </svg>
);

/** Filled chevron. Points left to hide the rail; rotate 180deg to show it again. */
export const IconCollapse = (p: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden {...p}>
    <path d="M14.8 4.6 7.2 12l7.6 7.4-1.6 1.6L4 12l9.2-9 1.6 1.6Z" />
  </svg>
);

export const IconSearch = (p: IconProps) => (
  <svg {...stroke(p)}>
    <circle cx="11" cy="11" r="7" />
    <path d="M16.5 16.5 21 21" />
  </svg>
);

export const IconSliders = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M4 8h10M18 8h2M4 16h2M10 16h10" />
    <circle cx="16" cy="8" r="2" />
    <circle cx="8" cy="16" r="2" />
  </svg>
);

export const IconCheck = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M4 12.5l5 5 11-11" />
  </svg>
);

export const IconMail = (p: IconProps) => (
  <svg {...stroke(p)}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="M4 7l8 6 8-6" />
  </svg>
);

export const IconKey = (p: IconProps) => (
  <svg {...stroke(p)}>
    <circle cx="8" cy="8" r="4" />
    <path d="M11 11l9 9M17 17l2-2M14 14l2-2" />
  </svg>
);

export const IconShield = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export const IconBell = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </svg>
);

export const IconPalette = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M12 3a9 9 0 1 0 0 18c1.5 0 2-1 2-2 0-1.5 1-2 2-2h2a3 3 0 0 0 3-3 8 8 0 0 0-2.34-5.66A9 9 0 0 0 12 3Z" />
    <circle cx="8" cy="11" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="12" cy="8" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="16" cy="11" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

export const IconDownload = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M12 4v11M8 11l4 4 4-4M5 19h14" />
  </svg>
);

export const IconTrash = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
  </svg>
);

export const IconLogout = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4M16 16l4-4-4-4M20 12H9" />
  </svg>
);

export const IconDoc = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5M10 13h6M10 16h6" />
  </svg>
);

export const IconGear = (p: IconProps) => (
  <svg {...stroke(p)}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14.2 2H9.8l-.4 2.5a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12a7 7 0 0 0 .1 1.2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2 1.2l.4 2.5h4.4l.4-2.5a7 7 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5A7 7 0 0 0 19 12Z" />
  </svg>
);

export const IconBrain = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-1 5 3 3 0 0 0 2 4 3 3 0 0 0 3 2V4ZM15 4a3 3 0 0 1 3 3 3 3 0 0 1 1 5 3 3 0 0 1-2 4 3 3 0 0 1-3 2V4Z" />
  </svg>
);

export const IconLink = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M9.5 14.5l5-5M8 11l-2 2a3.5 3.5 0 0 0 5 5l2-2M16 13l2-2a3.5 3.5 0 0 0-5-5l-2 2" />
  </svg>
);

export const IconMoon = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M20 14a8 8 0 0 1-10-10 8 8 0 1 0 10 10Z" />
  </svg>
);

export const IconPen = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M5 19l1-4L16 5l3 3L9 18l-4 1Z" />
    <path d="M14 7l3 3" />
  </svg>
);

export const IconPlus = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/** Corners pulling apart — open the composer into a full writing area. */
export const IconExpand = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M14 4h6v6M10 20H4v-6" />
    <path d="M20 4l-7 7M4 20l7-7" />
  </svg>
);

/** The same corners pulling in — back to the one-line composer. */
export const IconShrink = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M20 10h-6V4M4 14h6v6" />
    <path d="M14 10l6-6M10 14l-6 6" />
  </svg>
);

export const IconFolder = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M4 7a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Z" />
  </svg>
);

export const IconFolderPlus = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M4 7a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Z" />
    <path d="M12 11v5M9.5 13.5h5" />
  </svg>
);

export const IconShards = (p: IconProps) => (
  <svg {...stroke(p)}>
    <path d="M11 3 7 11l4 1 1-9Z" />
    <path d="m14 5 1 5 4-2-5-3Z" />
    <path d="M8 14l-3 6 6-2-1-4Z" />
    <path d="m15 13 4 1-2 5-3-3 1-3Z" />
  </svg>
);

/**
 * The app icon — the same mark that sits on someone's home screen.
 *
 * A PNG rather than an inline SVG: it is a stack of translucent gradient swirls, which as SVG is
 * several <defs> for no gain, and it has fixed brand colours so there is nothing for `currentColor`
 * to do. The asset is cropped to the mark itself and transparent throughout, including the hollow
 * centre, so `width` is the size it appears at and both Dusk and Dawn show through it.
 */
export const OreiaMark = ({ className, size = 24 }: { className?: string; size?: number }) => (
  <img
    className={className ? `omark ${className}` : 'omark'}
    src="/brand/oreia-mark.png"
    alt="Oreia"
    width={size}
    height={size}
  />
);

/** The sparkle. Still the mark on the auth screens, where it is set against nothing else. */
export const OreiaGlyph = (p: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...p}>
    <path
      d="M12 1.5c.5 4.2 2.3 6 6.5 6.5-4.2.5-6 2.3-6.5 6.5-.5-4.2-2.3-6-6.5-6.5 4.2-.5 6-2.3 6.5-6.5Z"
      opacity="0.9"
    />
    <circle cx="19" cy="18" r="1.5" opacity="0.6" />
  </svg>
);
